import {PagingApiService} from '../paging-api.service';
import {OnDestroy, OnInit} from '@angular/core';
import {untilDestroyed} from 'ngx-take-until-destroy';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';

/**
 * Компонент для списочного постраничного представления сущностей
 */
export abstract class ListItemsComponent<T extends PagingApiService = PagingApiService, ValuesToSelect = number>
    implements OnInit, OnDestroy {

    protected constructor(
        protected listService: T ,
    ) {}

    get service(): T {
        return this.listService as T;
    }

    /**
     * Коллекция выбранных значений (чаще всего это будет список id элементов)
     */
    selectedValues$ = new BehaviorSubject<ValuesToSelect[]>([]);

    public isCurrentItemsSelected$ = new BehaviorSubject<boolean>(false);

    toggleValueSelection(value: ValuesToSelect) {
        const currentValue: ValuesToSelect[] = this.selectedValues$.getValue();
        let newValue: ValuesToSelect[] = [...currentValue];

        if (currentValue.includes(value)) {
            newValue = newValue.filter(v => v !== value);
        } else {
            newValue.push(value);
        }

        this.selectedValues$.next(newValue);
    }

    selectPageValues() {
        const items = this.service.items$.getValue();
        const currentValues = this.selectedValues$.getValue();

        if (items && items.length) {
            const ids: ValuesToSelect[] = items.map(item => item.id).filter(id => !currentValues.includes(id));
            this.selectedValues$.next(currentValues.concat(ids));
        }
    }

    clearAllValues() {
        this.selectedValues$.next([]);
    }

    clearPageValues() {
        const items = this.service.items$.getValue();
        const currentValues = this.selectedValues$.getValue();

        if (items && items.length) {
            const ids: ValuesToSelect[] = items.map(item => item.id);
            this.selectedValues$.next(currentValues.filter(id => !ids.includes(id)));
        }
    }

    togglePageSelectedValues() {
        if (this.isCurrentItemsSelected$.getValue()) {
            this.clearPageValues();
        } else {
            this.selectPageValues();
        }
    }

    ngOnInit(): void {
        this.service.afterListComponentInit();
        this.trackSelectedItems();
    }

    ngOnDestroy(): void {
        this.selectedValues$.complete();
        this.isCurrentItemsSelected$.complete();
    }

    protected trackSelectedItems() {
        combineLatest([
            this.listService.items$,
            this.selectedValues$,
        ]).pipe(
            map((value: [{id: number}[], ValuesToSelect[]]) => this.isCurrentItemsSelected(...value)),
            untilDestroyed(this),
        ).subscribe((v) => this.isCurrentItemsSelected$.next(v));
    }

    protected isCurrentItemsSelected(items: {id: number}[], currentSelected: ValuesToSelect[]) {
        if (!items || !items.length) {
            return false;
        }

        const selected: number[] = currentSelected.map(item => Number(item));
        const ids: number[] = items.map(item => item.id);
        return ids.every(id => selected.includes(id));
    }
}
